import React, { useCallback, useEffect } from 'react';

import ym from 'react-yandex-metrika';

import {
    TeacherGptChat,
    setTeacherGptChatSuggestions,
    useTeacherGptChatHistoryInSessionStorage,
    TeacherGptChatMessage,
    TeacherGptSuggestionGroup,
    TeacherGptScenarioSuggestWithGroup,
    TeacherGptChatThrottledMessageChangeInfo,
    TeacherGptChatMode,
    DEFAULT_PREFIX_KEY_FOR_TEACHER_CHAT_GPT,
} from '@edu-frontend/modules/src/features/teacherGptChat';
import { hrLight } from '@edu-frontend/modules/src/components/ui/Theme';

import { postGptAnswer } from '@yandex-int/k-common/client/api/defs/post-gpt-answer';
import { postGptHelp } from '@yandex-int/k-common/client/api/defs/post-gpt-help';
import { useDispatch, useSelector } from 'react-redux';
import { getTeacherChatGptSettings } from 'platform-components/src/components/app-factory/selectors';
import { showNetworkError, useMe } from 'platform-components';

interface Props {
    className?: string | undefined;
}

export function TeacherGptChatWrapper({ className }: Props) {
    const user_id = useMe('id');

    useTeacherGptChatHistoryInSessionStorage(DEFAULT_PREFIX_KEY_FOR_TEACHER_CHAT_GPT + user_id);

    const suggests = useSelector(getTeacherChatGptSettings)?.suggests;

    useEffect(() => {
        setTeacherGptChatSuggestions(suggests || { lesson: [], help: [] });
    }, [suggests]);

    const dispatch = useDispatch();

    const askGpt = useCallback(
        async function askGpt(mode: TeacherGptChatMode, text: string, history: TeacherGptChatMessage[]) {
            ym('reachGoal', 'teacher_ai_helper_send_message', { user_id, text, mode });

            try {
                switch (mode) {
                    case 'lesson': {
                        const all = history.flatMap((x) =>
                            x.author === 'default'
                                ? []
                                : { role: x.author === 'gpt' ? ('assistant' as const) : ('user' as const), text: x.msg }
                        );
                        all.push({ role: 'user', text });

                        const res = await postGptAnswer(all);

                        return { mode, msg: res.alternatives[0].message.text };
                    }

                    case 'help': {
                        const { text: msg, thread_id: threadId } = await postGptHelp({
                            text,
                            threadId: history[history.length - 1]?.threadId,
                        });

                        return { mode, msg, threadId };
                    }

                    default:
                        return { mode, msg: 'Unexpected request mode' };
                }
            } catch (e) {
                dispatch(showNetworkError());

                throw e;
            }
        },
        [dispatch, user_id]
    );

    const onOpenClick = useCallback(() => {
        ym('reachGoal', 'teacher_ai_helper_open', { user_id });
    }, [user_id]);

    const onModeSwitch = useCallback(
        (mode: TeacherGptChatMode) => {
            ym('reachGoal', 'teacher_ai_helper_toggle_mode', { user_id, mode });
        },
        [user_id]
    );

    const onThrottledMessageChange = useCallback(
        (
            mode: TeacherGptChatMode,
            { userText: user_msg, actualText: actual_msg, reason }: TeacherGptChatThrottledMessageChangeInfo
        ) => {
            ym('reachGoal', 'teacher_ai_helper_input_text', { user_id, mode, user_msg, actual_msg, reason });
        },
        [user_id]
    );

    const onSuggestToggle = useCallback(
        (mode: TeacherGptChatMode, action: 'expand' | 'collapse') => {
            ym('reachGoal', 'teacher_ai_helper_toggle_suggest', { user_id, mode, action });
        },
        [user_id]
    );

    const onSuggestSectionSwitch = useCallback(
        (mode: TeacherGptChatMode, { groupName: theme }: TeacherGptSuggestionGroup) => {
            ym('reachGoal', 'teacher_ai_helper_choose_theme', { mode, user_id, theme });
        },
        [user_id]
    );

    const onSuggestClick = useCallback(
        (mode: TeacherGptChatMode, { group: theme, text: suggest }: TeacherGptScenarioSuggestWithGroup) => {
            ym('reachGoal', 'teacher_ai_helper_choose_suggest', { user_id, mode, theme, suggest });
        },
        [user_id]
    );

    return (
        <TeacherGptChat
            askGpt={askGpt}
            className={className}
            onModeSwitch={onModeSwitch}
            onOpenClick={onOpenClick}
            onSuggestClick={onSuggestClick}
            onSuggestSectionSwitch={onSuggestSectionSwitch}
            onSuggestToggle={onSuggestToggle}
            onThrottledMessageChange={onThrottledMessageChange}
            submitOnEnter={true}
            theme={hrLight}
        />
    );
}
